import { useCallback } from 'react';

import { intersection, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { useAuthenticateMutation } from '@modules/rtk/auth-api';
import { organizationPositionSel } from '@shared/duck/selectors/general.selectors';
import userServices from '@shared/duck/userServices';

import { loginError, loginPending, loginResponse } from './actions';
import { handleAuthenticateError } from './utils';

export function usePositions() {
  const orgInfo = useSelector(organizationPositionSel);

  /**
   * Checks if the user has any of the given organization positions.
   *
   * Usage:
    ```
    const { hasPositions } = usePositions();
    const isTroopAdminOrKey3 = hasPositions(['Key 3 Delegate', 'Troop Admin']);
    ```
   */
  const hasPositions = useCallback(
    (positions: string[]) => {
      const common = intersection(orgInfo.allRoles, positions);
      const found = !isEmpty(common);

      return found;
    },
    [orgInfo],
  );

  return { hasPositions };
}

/**
 * Username/password login. Replaces loginRequest/loginEpic$ so the caller can
 * send a recaptcha token.
 */
export function useLogin() {
  const dispatch = useDispatch();
  const [authenticate] = useAuthenticateMutation();

  const login = useCallback(
    async (username: string, password: string, recaptchaToken?: string) => {
      dispatch(loginPending(username));
      try {
        const data = await authenticate({
          username,
          body: { password, recaptchaToken },
        }).unwrap();

        const loginData = userServices.addSessionExp(data);
        userServices.storeLoginData(loginData);
        userServices.storeLogoutFlag(false);
        dispatch(loginResponse(loginData));
      } catch (error) {
        handleAuthenticateError(
          error as Parameters<typeof handleAuthenticateError>[0],
        );
        dispatch(loginError(error));
      }
    },
    [dispatch, authenticate],
  );

  return { login };
}
